import React, { useState, useEffect } from 'react';
import { inpatientAPI } from '../../services/api';
import { pdfServices } from '../../services/pdfservices';
import SelectPaymentModal from './SelectPaymentModal';
import { useTranslation } from "react-i18next";

const CloseInpatientModal = ({
  isOpen,
  onClose,
  inpatientId,
  onSuccess,
  paymentAmount,
  totalAmount,
  downpayment
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [invoiceResult, setInvoiceResult] = useState(null);
  const [billing, setBilling] = useState(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setError('');
      setShowPaymentModal(false);
      setInvoiceResult(null);
      setBilling(null);

      // Pull the real billing snapshot (total / paid so far / remaining)
      if (inpatientId) {
        inpatientAPI.getInpatientBilling(inpatientId)
          .then((res) => setBilling(res?.data?.data || null))
          .catch(() => setBilling(null));
      }
    }
  }, [isOpen, inpatientId]);

  const doClose = async (paymentData = null) => {
    setLoading(true);
    setError('');

    const closeData = { status: 'completed' };
    if (paymentData) {
      closeData.payment_type = paymentData.method;
      closeData.payment_note = paymentData.notes;
      if (paymentData.amount != null) {
        closeData.amount = paymentData.amount;
      }
    }

    try {
      const response = await inpatientAPI.closeInpatientVisit(inpatientId, closeData);
      const invoice = response?.data?.data?.invoice || null;

      // Fresh billing so the result screen can list every payment made,
      // including the one just collected at close.
      let paymentsList = [];
      try {
        const billingRes = await inpatientAPI.getInpatientBilling(inpatientId);
        paymentsList = billingRes?.data?.data?.payments || [];
      } catch (_) {
        paymentsList = [];
      }

      setInvoiceResult({
        invoice_number: invoice?.invoice_number || null,
        total: invoice?.total ?? totalAmount ?? 0,
        paid: invoice?.paid ?? (paymentData ? paymentAmount : 0),
        balance: invoice?.balance ?? (paymentData ? 0 : paymentAmount),
        currency: invoice?.currency || 'USD',
        payments: paymentsList,
      });
    } catch (err) {
      console.error('Error closing inpatient visit:', err);
      setError(err.response?.data?.message || t("receptionInpatients.close.errors.generic"));
      setShowPaymentModal(false);
    } finally {
      setLoading(false);
    }
  };

  // Close now, no payment captured — the visit stays on the client's balance.
  const handleCloseVisit = async () => {
    await doClose(null);
  };

  const handlePaymentSave = async (paymentData) => {
    setShowPaymentModal(false);
    await doClose(paymentData);
  };

  const handleDone = () => {
    if (onSuccess) {
      onSuccess();
    }
    onClose();
  };

  const handleDownloadInvoice = async () => {
    if (downloadingInvoice) return;
    setDownloadingInvoice(true);
    try {
      const response = await pdfServices.generateInvoice(inpatientId, 'inpatient');
      const disposition = response.headers?.['content-disposition'];
      let fileName = `invoice-${inpatientId}.pdf`;
      const match = disposition?.match(/filename="?([^"]+)"?/);
      if (match) fileName = match[1];
      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Error downloading invoice:', err);
    } finally {
      setDownloadingInvoice(false);
    }
  };


  if (!isOpen) return null;

  // Prefer the live billing snapshot over the (possibly stale) row props
  const billTotal = billing ? parseFloat(billing.total) : (parseFloat(totalAmount) || 0);
  const billDown = billing ? parseFloat(billing.downpayment) : (parseFloat(downpayment) || 0);
  const billPaid = billing ? parseFloat(billing.paid) : billDown;
  const billRemaining = billing ? parseFloat(billing.remaining) : (parseFloat(paymentAmount) || 0);
  const billCurrency = billing?.currency || 'USD';
  const formatMoney = (v) => `${billCurrency} ${parseFloat(v || 0).toFixed(2)}`;
  const money = (v) => `${invoiceResult?.currency || billCurrency} ${parseFloat(v || 0).toFixed(2)}`;

  // Result screen — invoice issued, show number + balance + print action
  if (invoiceResult) {
    return (
      <div
        className="reception-modal-overlay"
        dir={i18n.dir()}
        lang={i18n.language}
        style={{ direction: isRTL ? 'rtl' : 'ltr' }}
      >
        <div className="reception-modal">
          <div className="reception-modal-header" style={{ flexDirection: isRTL ? 'row-reverse' : 'row' }}>
            <h2 className="reception-modal-title" style={{ textAlign: isRTL ? 'right' : 'left' }}>
              {t("receptionInpatients.close.header.title")}
            </h2>
          </div>
          <div className="reception-modal-content">
            <div className="confirmation-message" style={{ textAlign: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1F2937', marginBottom: '8px' }}>
                {t('receptionOutpatients.close.result.heading', 'Visit closed')}
              </h3>
              {invoiceResult.invoice_number && (
                <p style={{ fontSize: '14px', color: '#6B7280', margin: '0 0 16px' }}>
                  {t('receptionOutpatients.close.result.invoiceIssued', 'Invoice {{number}} issued', { number: invoiceResult.invoice_number })}
                </p>
              )}
            </div>

            <div style={{
              background: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              padding: '16px 20px',
              margin: '0 0 20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}>
              {/* Itemized payments with dates — same view as the pre-close summary */}
              {invoiceResult.payments?.length > 0 && (
                <>
                  {invoiceResult.payments.map((p) => (
                    <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <span style={{ color: '#374151', fontWeight: 600 }}>
                          {p.date} {p.method && `· ${p.method}`}
                        </span>
                        {p.type === 'downpayment' && (
                          <span style={{ fontSize: '11px', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                            {t('receptionOutpatients.close.payment.downpayment')}
                          </span>
                        )}
                      </div>
                      <span style={{ fontWeight: 700, color: '#1F2937' }}>
                        {money(p.amount)}
                        {p.discount > 0 && (
                          <span style={{ fontSize: '11px', color: '#B45309', fontWeight: 600, marginLeft: '6px' }}>
                            (+{money(p.discount)} {t('receptionOutpatients.close.paymentModal.discountShort', 'discount')})
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
                  <div style={{ borderTop: '1px solid #E5E7EB' }} />
                </>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                <span style={{ color: '#6B7280' }}>{t("receptionInpatients.close.summary.total")}</span>
                <span style={{ fontWeight: 700, color: '#1F2937' }}>{money(invoiceResult.total)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                <span style={{ color: '#6B7280' }}>{t('receptionOutpatients.close.payment.paid', 'Paid')}</span>
                <span style={{ fontWeight: 700, color: '#059669' }}>{money(invoiceResult.paid)}</span>
              </div>
              <div style={{
                display: 'flex', justifyContent: 'space-between', fontSize: '14px',
                borderTop: '1px solid #E5E7EB', paddingTop: '10px',
              }}>
                <span style={{ color: '#6B7280' }}>{t("receptionInpatients.close.summary.due")}</span>
                <span style={{ fontWeight: 800, color: invoiceResult.balance > 0 ? '#B91C1C' : '#059669' }}>
                  {money(invoiceResult.balance)}
                </span>
              </div>
            </div>

            <div className="reception-modal-actions">
              <button
                type="button"
                onClick={handleDownloadInvoice}
                disabled={downloadingInvoice}
                style={{
                  padding: '10px 18px',
                  borderRadius: 10,
                  border: '2px solid #8548A8',
                  background: '#fff',
                  cursor: downloadingInvoice ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#8548A8',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  opacity: downloadingInvoice ? 0.7 : 1,
                  flexDirection: isRTL ? 'row-reverse' : 'row'
                }}
              >
                {downloadingInvoice ? (
                  <span className="btn-spinner" />
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                )}
                {downloadingInvoice
                  ? t('receptionInpatients.close.buttons.generating', 'Generating...')
                  : t('receptionOutpatients.close.buttons.downloadInvoice', 'Download invoice')}
              </button>
              <button
                type="button"
                className="reception-action-btn"
                onClick={handleDone}
                style={{
                  background: 'linear-gradient(135deg, #10B981, #059669)',
                  color: '#fff',
                }}
              >
                {t('common.done', 'Done')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="reception-modal-overlay"
      dir={i18n.dir()}
      lang={i18n.language}
      style={{ direction: isRTL ? 'rtl' : 'ltr' }}
    >
      <div className="reception-modal">
        <div className="reception-modal-header" style={{ flexDirection: isRTL ? 'row-reverse' : 'row' }}>
          <h2 className="reception-modal-title" style={{ textAlign: isRTL ? 'right' : 'left' }}>
            {t("receptionInpatients.close.header.title")}
          </h2>
        </div>
        <div className="reception-modal-content">
          {/* Main Content */}
          <div className="confirmation-message" style={{ textAlign: 'center', marginBottom: '24px' }}>

            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1F2937', marginBottom: '8px' }}>
              {t("receptionInpatients.close.header.subtitle")}
            </h3>
            <p style={{ fontSize: '16px', color: '#6B7280', lineHeight: '1.5', marginBottom: '16px' }}>
              {t("receptionInpatients.close.header.description")}
            </p>
          </div>

          {/* Payment summary — same row-style view as the post-close result screen */}
          <div style={{
            background: '#FFFFFF',
            border: '1px solid #E5E7EB',
            borderRadius: '12px',
            padding: '20px',
            margin: '8px 0 24px 0'
          }}>
            {/* Each payment with its date */}
            {billing?.payments?.length > 0 && (
              <>
                {billing.payments.map((p) => (
                  <div key={p.id} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: '1px solid #F3F4F6'
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span style={{ fontSize: '14px', color: '#374151', fontWeight: 600 }}>
                        {p.date} {p.method && `· ${p.method}`}
                      </span>
                      {p.type === 'downpayment' && (
                        <span style={{ fontSize: '11px', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                          {t("receptionInpatients.close.summary.downpayment")}
                        </span>
                      )}
                    </div>
                    <span style={{ fontSize: '14px', fontWeight: 700, color: '#111827' }}>
                      {formatMoney(p.amount)}
                      {p.discount > 0 && (
                        <span style={{ fontSize: '11px', color: '#B45309', fontWeight: 600, marginLeft: '6px' }}>
                          (+{formatMoney(p.discount)} {t('receptionOutpatients.close.paymentModal.discountShort', 'discount')})
                        </span>
                      )}
                    </span>
                  </div>
                ))}
                <div style={{ borderTop: '2px solid #E5E7EB', margin: '12px 0' }} />
              </>
            )}

            {/* Total / Paid / Remaining rows */}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
              <span style={{ fontSize: '15px', color: '#6B7280' }}>{t("receptionInpatients.close.summary.total")}</span>
              <span style={{ fontSize: '15px', fontWeight: 700, color: '#111827' }}>{formatMoney(billTotal)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
              <span style={{ fontSize: '15px', color: '#6B7280' }}>{t('receptionOutpatients.close.payment.paidSoFar', 'Paid so far')}</span>
              <span style={{ fontSize: '15px', fontWeight: 700, color: '#059669' }}>{formatMoney(billPaid)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
              <span style={{ fontSize: '15px', color: '#6B7280' }}>{t("receptionInpatients.close.summary.due")}</span>
              <span style={{ fontSize: '17px', fontWeight: 800, color: billRemaining > 0.001 ? '#DC2626' : '#059669' }}>
                {formatMoney(billRemaining)}
              </span>
            </div>
          </div>

          {error && (
          <div className="error-message" style={{ textAlign: isRTL ? 'right' : 'left' }}>
              {error}
            </div>
          )}

          <div className="reception-modal-actions">
            <button
              onClick={() => onClose && onClose()}
              className="payment-modal-cancel-btn"
              style={{
                padding: "10px 18px",
                borderRadius: 10,
                border: "2px solid #e2e8f0",
                background: "linear-gradient(135deg, #FFFFFF 0%, #f8fafc 100%)",
                cursor: "pointer",
                opacity: 1,
                fontSize: 13,
                fontWeight: 600,
                color: "#64748b",
                transition: "all 0.3s ease",
                display: "flex",
                alignItems: "center",
                gap: 6,
                flexDirection: isRTL ? 'row-reverse' : 'row'
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
              {t("common.cancel")}
            </button>
            <button
              type="button"
              onClick={() => (billRemaining > 0.001 ? setShowPaymentModal(true) : handleCloseVisit())}
              disabled={loading}
              style={{
                padding: "10px 18px",
                borderRadius: 10,
                border: "2px solid #3B82F6",
                background: "linear-gradient(135deg, #3B82F6, #2563EB)",
                cursor: "pointer",
                opacity: loading ? 0.6 : 1,
                fontSize: 13,
                fontWeight: 600,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                gap: 6,
                flexDirection: isRTL ? 'row-reverse' : 'row',
                boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)"
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="5" width="20" height="14" rx="2" />
                <path d="M2 10h20" />
              </svg>
              {billRemaining > 0.001
                ? t('receptionOutpatients.close.buttons.recordPaymentClose', 'Add Payment & Close')
                : t('receptionOutpatients.close.buttons.closeVisit', 'Close Visit')}
            </button>
            {/* Commented out payment modal button - will be used later */}
            {/* 
            <button
              type="button"
              className="reception-action-btn"
              onClick={handleProceedToPayment}
              disabled={loading}
              style={{
                background: "linear-gradient(135deg, #3B82F6, #2563EB)",
                color: "#fff",
                boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)"
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "linear-gradient(135deg, #2563EB, #1D4ED8)";
                e.target.style.transform = "translateY(-1px)";
                e.target.style.boxShadow = "0 6px 16px rgba(59, 130, 246, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "linear-gradient(135deg, #3B82F6, #2563EB)";
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.3)";
              }}
            >
              {loading ? 'Processing...' : 'Proceed to Payment'}
            </button>
            */}
          </div>
        </div>
      </div>

      <SelectPaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSave={handlePaymentSave}
        price={billRemaining}
        currency={billCurrency}
        totalAmount={billTotal}
        dueAmount={billRemaining}
        allowAmount={true}
        title={t('receptionOutpatients.close.paymentModal.title', 'Record payment')}
        isLoading={loading}
      />


      {/* CSS Animations */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
   
        
        .confirmation-message {
          text-align: center;
        }
        
        .confirmation-icon {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-bottom: 16px;
        }
        
        .error-message {
          background: #FEF2F2;
          border: 1px solid #FECACA;
          color: #DC2626;
          padding: 12px 16px;
          border-radius: 8px;
          margin: 16px 0;
          font-size: 14px;
          font-weight: 500;
        }
      `}</style>
    </div>
  );
};

export default CloseInpatientModal;
